/**
 * 廠商搜尋 modal(REQ-20260522 Phase 1B-Console)。
 * 簡化版 — 不做欄位選擇,單純 q 字串 + 列表。
 * 清單支援 ↑ / ↓ + Enter 選定(REQ-20260903010)。
 * 每頁 50 筆並可翻頁(REQ-20260916002)—— 廠商已逾千家,只看前 50 筆等於看不到後面的。
 * 四個欄位標題可點排序(REQ-20260916003);**排序走後端**(sort / order 當查詢參數送),
 * 因為清單是後端分頁的,只排當頁 50 筆排出來的順序是錯的(ui-guidelines §2)。
 */
import { useEffect, useRef, useState } from 'react';
import type { SupplierListQuery } from '@app/shared';
import { useListKeyboardNav } from '../../components/useListKeyboardNav';
import { useReferences } from '../settings/api';
import { useSuppliers } from './api';

export interface SupplierSearchModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (id: number) => void;
  /**
   * 有傳時:**搜尋框留白**的預設清單只列這個廠商類別(REQ-20260829002)。
   * 使用者一輸入搜尋字串就不套用,手動搜尋永遠查得到全部廠商。
   */
  defaultCategoryName?: string;
}

const PAGE_SIZE = 50;

/** 這張表可點排序的四欄 —— 取自後端接受的 sort 值,不另外定義一套字串 */
type SupplierSearchSortKey = Extract<
  SupplierListQuery['sort'],
  'code' | 'simpleName' | 'categoryName' | 'tax'
>;

export function SupplierSearchModal({
  open,
  onClose,
  onPick,
  defaultCategoryName,
}: SupplierSearchModalProps): JSX.Element | null {
  const [q, setQ] = useState('');
  const trimmed = q.trim();
  // 分頁只放本地 state,不寫進網址 —— 這是彈窗而不是頁面(REQ-20260916002)
  const [page, setPage] = useState(1);
  // 排序同樣只放本地 state(REQ-20260916003)。預設維持 simpleName / asc,
  // 初次開窗的順序與加排序之前逐字相同。
  const [sortKey, setSortKey] = useState<SupplierSearchSortKey>('simpleName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const categories = useReferences('supplier-categories', true);
  const defaultCategoryId =
    defaultCategoryName == null
      ? undefined
      : (categories.data?.data ?? []).find((r) => r.name.trim() === defaultCategoryName)?.id;

  // 只有「留白 + 找得到同名類別」才套篩選。找不到(還沒建 / 名稱不同 / 還在載入 / 載入失敗)
  // 就退回列全部廠商 —— 寧可多列,也不要讓該選的廠商選不到。
  const appliedCategoryId = trimmed.length === 0 ? defaultCategoryId : undefined;

  const query = useSuppliers({
    page,
    pageSize: PAGE_SIZE,
    sort: sortKey,
    order: sortDir,
    ...(trimmed.length > 0 ? { q: trimmed } : {}),
    ...(appliedCategoryId != null ? { categoryId: appliedCategoryId } : {}),
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const nav = useListKeyboardNav(rows.length, (i) => {
    const s = rows[i];
    if (s) onPick(s.id);
  });

  // 換排序:同欄再點翻轉方向,換欄重設為 asc(照抄 DashboardPage 的 onSort)。
  // 排序是後端做的,頁碼要回第 1 頁;高亮也要清掉 —— useListKeyboardNav 只在 `count`
  // 變化時重設,換排序後筆數常常還是 50,高亮會停在舊的那一列(REQ-20260903010)。
  const onSort = (key: SupplierSearchSortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
    nav.setIndex(-1);
  };

  // 開啟時清空 q,並把分頁、排序與高亮歸零 —— 關閉時元件不卸載,狀態會留到下次開啟
  useEffect(() => {
    if (open) {
      setQ('');
      setPage(1);
      setSortKey('simpleName');
      setSortDir('asc');
      nav.setIndex(-1);
    }
  }, [open]);

  // 關鍵字一變就回第一頁:留在第 3 頁換關鍵字往往只會看到空清單。
  // 高亮也要一起清掉,否則指的是上一份清單的第 N 列。
  useEffect(() => {
    setPage(1);
    nav.setIndex(-1);
  }, [trimmed]);

  // 高亮跑出可視範圍時把該列捲進來(jsdom 沒有 scrollIntoView,要 optional chaining)
  const activeId = nav.index >= 0 ? rows[nav.index]?.id : undefined;
  useEffect(() => {
    if (activeId != null) rowRefs.current[activeId]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-16 px-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="搜尋廠商"
        className="bg-white rounded shadow-lg w-full max-w-2xl p-4 grid gap-3 max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">搜尋廠商</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            className="text-slate-500 hover:text-slate-800"
          >
            ✕
          </button>
        </div>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            nav.onKeyDown(e);
          }}
          placeholder="輸入編號 / 公司名 / 統編 / 電話 / 類別 / 備註 / 品項 …"
          className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <div className="border rounded overflow-auto" style={{ maxHeight: '50vh' }}>
          {query.isLoading ? (
            <div className="p-3 text-sm text-slate-500">載入中…</div>
          ) : query.isError ? (
            <div className="p-3 text-sm text-red-700">載入失敗</div>
          ) : rows.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">
              {trimmed ? '查無符合' : '請輸入搜尋條件'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-xs text-slate-600 border-b">
                  <SortableTh
                    className="px-3 py-1.5 w-32"
                    label="編號"
                    k="code"
                    active={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  {/* 這一欄排的是 simpleName —— 全名只是底下的副標,畫面上的主要名稱是簡稱 */}
                  <SortableTh
                    className="px-3 py-1.5"
                    label="簡稱 / 全名"
                    k="simpleName"
                    active={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  {/* 類別自 REQ-20260916002 起也是關鍵字比對的欄位之一,
                      不顯示的話使用者看不懂某一列為什麼會被搜出來 */}
                  <SortableTh
                    className="px-3 py-1.5 w-28"
                    label="類別"
                    k="categoryName"
                    active={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  <SortableTh
                    className="px-3 py-1.5 w-32"
                    label="統一編號"
                    k="tax"
                    active={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((s, i) => (
                  <tr
                    key={s.id}
                    ref={(el) => {
                      rowRefs.current[s.id] = el;
                    }}
                    // hover 變體會蓋掉高亮底色,所以兩者擇一輸出而不是並存
                    className={`border-b last:border-b-0 cursor-pointer ${
                      i === nav.index ? 'bg-blue-100' : 'hover:bg-blue-50'
                    }`}
                    aria-selected={i === nav.index ? true : undefined}
                    data-testid={i === nav.index ? 'kbd-active' : undefined}
                    onClick={() => onPick(s.id)}
                  >
                    <td className="px-3 py-1.5 font-mono text-xs">{s.code ?? '—'}</td>
                    <td className="px-3 py-1.5">
                      <div>{s.simpleName}</div>
                      <div className="text-xs text-slate-500">{s.fullName}</div>
                    </td>
                    <td className="px-3 py-1.5">{s.categoryName ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{s.tax ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {appliedCategoryId != null ? (
          <div className="text-xs text-slate-500">
            <p>預設只列出「{defaultCategoryName}」類別的廠商,輸入搜尋條件即可查全部廠商。</p>
          </div>
        ) : null}
        {/* 版面與 class 照抄 ProductListPage 的分頁區塊;只有一頁時也照樣顯示,
            讓「共 N 筆」永遠看得到(mt-4 省掉:外層是 grid gap-3,會變兩倍間距) */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-500">共 {total} 筆</span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => {
                setPage(Math.max(1, page - 1));
                nav.setIndex(-1);
              }}
              className="border rounded px-3 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              上一頁
            </button>
            <span className="px-3 py-1">{page} / {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => {
                setPage(Math.min(totalPages, page + 1));
                nav.setIndex(-1);
              }}
              className="border rounded px-3 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              下一頁
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 可排序表頭(REQ-20260916003)—— 樣式、箭頭符號、藍 / 灰配色與 aria-label
 * 照抄 `DashboardPage` 的 `SortableTh`(ui-guidelines §2),沒有發明新的互動。
 * `className` 由呼叫端整串帶入,因為各欄還有既有的寬度 class(w-32 / w-28)。
 * 只有這支彈窗用得到,不抽成共用模組。
 */
function SortableTh({
  className,
  label,
  k,
  active,
  dir,
  onSort,
}: {
  className: string;
  label: string;
  k: SupplierSearchSortKey;
  active: SupplierSearchSortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SupplierSearchSortKey) => void;
}): JSX.Element {
  const isActive = active === k;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className="inline-flex items-center gap-1 hover:text-blue-600"
        aria-label={`依${label}排序`}
      >
        {label}
        <span className={isActive ? 'text-blue-600 text-xs' : 'text-slate-300 text-xs'}>
          {isActive && dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}
